import { Link } from './link'
import { Link as GatsbyLink } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import React, { FC, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { Container } from './container'
import { routes } from '../routes'
import { PageContext } from '../contexts/page-context'

interface HeaderProps {
  activePage: string
}

export const Header: FC<HeaderProps> = ({ activePage }) => {
  const { t } = useTranslation('header')

  const { lang, originalPath } = useContext(PageContext)

  const otherLanguage = useMemo(() => (lang === 'en' ? 'fr' : 'en'), [lang])

  return (
    <HeaderEl>
      <HeaderContainer>
        <Logo>
          <Link to="/">
            <StaticImage
              src="../assets/images/exclusive-logo.png"
              alt="Exclusive Maintenance"
              placeholder="blurred"
              width={160}
              className="img"
            />
          </Link>
        </Logo>
        <Nav>
          <input type="checkbox" />
          <span></span>
          <span></span>
          <span></span>

          <Menu>
            <MenuItem>
              <MenuLink to="/" isActive={activePage === '/'}>
                {t('home')}
              </MenuLink>
            </MenuItem>
            <MenuItem>
              <MenuLink to="/services/" isActive={activePage === '/services/'}>
                {t('services')}
              </MenuLink>
            </MenuItem>
            <MenuItem>
              <MenuLink to="/esg/" isActive={activePage === '/esg/'}>
                {t('esg')}
              </MenuLink>
            </MenuItem>
            <MenuItem>
              <MenuLink to="/jobs/" isActive={activePage === '/jobs/'}>
                {t('jobs')}
              </MenuLink>
            </MenuItem>
            <MenuItem>
              <MenuLink to="/contact/" isActive={activePage === '/contact/'}>
                {t('contact')}
              </MenuLink>
            </MenuItem>
            <MenuItem>
              <MenuLink
                as={GatsbyLink}
                to={`/${otherLanguage}${routes[originalPath][otherLanguage]}`}
              >
                {t('otherLng')}
              </MenuLink>
            </MenuItem>
          </Menu>
        </Nav>
      </HeaderContainer>
    </HeaderEl>
  )
}

const HeaderEl = styled.header`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    height: ${theme.spacing.headerHeight};
    align-items: center;
    transition: background 0.2s;
    z-index: 1000;
    color: ${theme.colors.light};
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    position: relative;
    z-index: 10;
  `}
`

const padding = '1.875rem'
const HeaderContainer = styled(Container)`
  ${({ theme }) => css`
    width: calc(100% - (${padding} * 2));
    padding: 0 ${padding};
    max-width: ${theme.spacing.maxWidth};
    margin-left: auto;
    margin-right: auto;
    display: flex;
    justify-content: space-between;
  `}
`

const Logo = styled.div`
  font-size: 2rem;
  text-transform: uppercase;
  align-self: center;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }

  .img {
    max-width: 10rem;
  }
`

const Nav = styled.nav`
  ${({ theme }) => css`
    align-self: center;

    ${Menu} {
      ${MenuItem} {
        ${MenuLink} {
          display: block;
          color: ${theme.colors.brand};
        }

        &:last-of-type {
          color: ${theme.colors.accent};
        }
      }
    }

    ${theme.media.max('tablet_w')} {
      ${Menu} {
        position: fixed;
        width: 100%;
        top: ${theme.spacing.headerHeight};
        left: 0;
        transform-origin: 0% 0%;
        transform: translate(100%, 0);
        background: ${theme.colors.white};
        padding-bottom: 1rem;

        transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1);
        z-index: 100;

        ${MenuItem} {
          text-align: left;
          transition: 0.2s;
          text-transform: uppercase;

          ${MenuLink} {
            padding: 0.715rem 2.625rem;

            &:last-of-type {
              font-size: 1.125rem;
            }
          }
        }
      }

      span {
        display: block;
        width: 1.875rem;
        height: 3px;
        margin-bottom: 6px;
        position: relative;
        background: ${theme.colors.brand};
        border-radius: 3px;
        z-index: 1;
        transform-origin: 4px 0px;
        transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
          background 0.5s cubic-bezier(0.77, 0.2, 0.05, 1), opacity 0.55s ease;

        &:first-of-type(2) {
          transform-origin: 0% 0%;
        }

        &:nth-of-type(3) {
          transform-origin: 0% 100%;
        }
      }

      input {
        display: block;
        width: 2rem;
        height: 2rem;
        position: absolute;

        cursor: pointer;

        opacity: 0;
        z-index: 2;

        -webkit-touch-callout: none;

        &:checked ~ span {
          opacity: 1;
          transform: rotate(45deg) translate(2px, 0px);

          &:nth-of-type(2) {
            opacity: 0;
            transform: rotate(0deg) scale(0.2, 0.2);
          }

          &:nth-of-type(3) {
            transform: rotate(-45deg) translate(0, 3px);
          }
        }

        &:checked ~ ${Menu} {
          transform: none;
        }
      }
    }

    ${theme.media.min('tablet_w')} {
      width: 100%;

      > input,
      > span {
        display: none;
      }
    }
  `}
`

const Menu = styled.ul`
  ${({ theme }) => css`
    color: ${theme.colors.text};

    ${theme.media.min('tablet_w')} {
      display: flex;
      color: ${theme.colors.light};
      transition: 0.2s ease-in-out;

      ${MenuItem} {
        display: inline-block;
        margin: 0 0 0 2.5rem;
        padding: 2rem 0;

        &:last-of-type {
          margin-left: auto;
        }
      }
    }
  `}
`

const MenuItem = styled.li``

interface LinkProps {
  isActive?: boolean
}
const MenuLink = styled(Link)<LinkProps>`
  ${({ theme, isActive }) => css`
    font-family: ${theme.typography.raleway};
    font-size: 1.5rem;
    text-transform: uppercase;
    text-decoration: none;

    ${theme.media.min('tablet_w')} {
      font-size: 1.125rem;
      padding: 0;
      border-bottom: 2px solid transparent;

      &:hover {
        border-bottom: 2px solid ${theme.colors.accent};
      }
      ${isActive &&
      css`
        border-bottom: 2px solid ${theme.colors.accent};
      `}
    }
  `}
`
